/** @jsxImportSource @emotion/react */
import React, { useCallback, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import IconButton from '@mui/material/IconButton'
import Switch from '@mui/material/Switch'
import { useApolloClient, useMutation } from '@apollo/client'
import orderBy from 'lodash/orderBy'
import { cmsRoutes, useLink } from 'routes/cmsRoutes'
import Table from 'components/Table'
import TableButtons from 'components/TableButtons/TableButtons'
import { ReactComponent as EditIcon } from 'assets/icons/edit.svg'
import { toast } from 'utils/toast'
import { BULK_UPDATE_AI_TEST, UPDATE_AI_TEST } from 'queries/responseTests'
import { AITestResponseTypeEnum } from '__generatedTypes__/globalTypes'
import { findOneAiFullLengthTest_findOneCasperFullLengthTest_aiTests as AITest } from '__generatedTypes__/findOneAiFullLengthTest'
import { updateAiTest, updateAiTestVariables } from '__generatedTypes__/updateAiTest'
import { bulkUpdateAiTest, bulkUpdateAiTestVariables } from '__generatedTypes__/bulkUpdateAiTest'
import * as responseTestsClasses from 'pagesNew/ResponseTests/styles'

type Props = {
  fltId: string
  allTests: AITest[]
  responseType: AITestResponseTypeEnum
}

// Similar to pagesNew/ResponseTests/TestsList.tsx
export const ResponseTestsSection = ({ fltId, allTests, responseType }: Props) => {
  const apolloClient = useApolloClient()
  const { navigate, getLink } = useLink()
  const [submittingTestId, setSubmittingTestId] = useState<string | undefined>()
  const [updateTest] = useMutation<updateAiTest, updateAiTestVariables>(UPDATE_AI_TEST)
  const [bulkUpdateTests] = useMutation<bulkUpdateAiTest, bulkUpdateAiTestVariables>(BULK_UPDATE_AI_TEST)
  const tests = useMemo(() => {
    const filteredTests = allTests.filter((test) => test.responseType === responseType)
    return orderBy(filteredTests, (a) => a.order, 'asc')
  }, [allTests, responseType])

  const handleAddTestClick = useCallback(() => {
    navigate(cmsRoutes.RESPONSE_TESTS_CREATE, undefined, {
      searchParams: new URLSearchParams({
        fltId,
        responseType,
        defaultOrder: String(tests.length),
      }),
    })
  }, [fltId, responseType, tests.length])

  const updateTestInCache = useCallback(
    (test: AITest, updatedFields: Partial<AITest>) => {
      const id = apolloClient.cache.identify({
        __typename: test.__typename,
        id: test.id,
      })

      if (!id) {
        return
      }

      const fieldModifiers = Object.fromEntries(Object.entries(updatedFields).map(([key, value]) => [key, () => value]))

      apolloClient.cache.modify({
        id,
        fields: fieldModifiers,
      })
    },
    [apolloClient]
  )

  const handleSort = useCallback(async (testUpd: AITest[]) => {
    try {
      const newOrder = testUpd.map((test, order) => ({ ...test, order }))

      newOrder.forEach((test) => {
        updateTestInCache(test, {
          order: test.order,
        })
      })

      await bulkUpdateTests({
        variables: {
          input: newOrder.map(({ id, order }) => ({ id, order })),
        },
      })

      toast.success('Tests order updated successfully')
    } catch (error) {
      toast.error('Failed to update tests order')
    }
  }, [])

  const handleIsLiveChange = useCallback(async (test: AITest, isLive: boolean) => {
    setSubmittingTestId(test.id)

    try {
      updateTestInCache(test, { isLive })

      await updateTest({
        variables: {
          input: {
            id: test.id,
            isLive,
          },
        },
      })
      toast.success('Test updated successfully')
    } catch (e) {
      updateTestInCache(test, { isLive: !isLive })

      const error = e as Error
      if (error.name === 'ApolloError') {
        toast.error(error.message)
      } else {
        toast.error('Failed to update Test')
      }
    }

    setSubmittingTestId(undefined)
  }, [])

  const getEditLink = useCallback(
    (testId: string) =>
      getLink(cmsRoutes.RESPONSE_TESTS_EDIT, {
        testId,
      }),
    []
  )

  return (
    <div css={responseTestsClasses.tablesWrapper}>
      <Table<AITest>
        css={responseTestsClasses.table}
        columns={[
          {
            title: 'Title',
            renderCell: (test) => test.title,
          },
          {
            title: 'Live',
            renderCell: (test) => (
              <Switch
                checked={test.isLive}
                onClick={(e) => e.stopPropagation()}
                onChange={(e, isLive) => handleIsLiveChange(test, isLive)}
                disabled={test.id === submittingTestId}
              />
            ),
            flex: '0 1 50px',
          },
          {
            renderCell: (test) => (
              <Link to={getEditLink(test.id)}>
                <IconButton size="small" css={responseTestsClasses.link}>
                  <EditIcon />
                </IconButton>
              </Link>
            ),
            flex: '0 1 30px',
          },
        ]}
        items={tests}
        onSort={handleSort}
        isSortable
      />
      <TableButtons itemName={'Response Test'} onAddItem={handleAddTestClick} />
    </div>
  )
}
